import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { HugeiconsIcon } from "@hugeicons/react";
import { ViewIcon, CancelCircleIcon } from "@hugeicons-pro/core-stroke-rounded";
import dayjs from "dayjs";
import { DataTable } from "@outpave/ds";
import type { DataColumn, RowAction } from "@outpave/ds";
import { GET_COMPANIES } from "../../../graphql/company";
import useInfiniteScroll from "hooks/useInfiniteScroll";
import { CompanyFilterCriteriaProps } from "pages/platform/Accounts";
import { DEFAULT_PAGINATION_SIZE } from "components/common/Constants";
import CompanyFilterType from "enums/CompanyFilterType";
import StatusType from "enums/StatusType";
import { Link as RouterLink } from "react-router-dom";
import { Link } from "@mui/material";
import { DeactivateAccountModal } from "./DeactivateAccountModal";
import { RenderAccountStatus } from "components/common/RenderAccountStatus";

type Account = {
  id: string;
  businessLegalName: string;
  numberOfUser: number;
  createdAt: string;
  status: StatusType;
  isDeactivateRequested: boolean;
};

type AccountTableProps = {
  handleOpenViewDetailsDrawer: (concactId: string) => void;
  filterCriteria: CompanyFilterCriteriaProps;
  refreshAccounts?: number;
};

const AccountsTable: React.FC<AccountTableProps> = (props) => {
  const { handleOpenViewDetailsDrawer, filterCriteria, refreshAccounts } =
    props;
  const [openDeactivateAccountModal, setOpenDeactivateAccountModal] =
    useState<boolean>(false);
  const [accountToDeactivate, setAccountToDeactivate] =
    useState<Account | null>(null);

  const { t } = useTranslation();

  const [defaultPaginationVariables, setDefaultPaginationVariables] = useState(
    {},
  );

  useEffect(() => {
    if (filterCriteria && Object.keys(filterCriteria).length > 0) {
      setDefaultPaginationVariables({
        first: DEFAULT_PAGINATION_SIZE,
        after: undefined,
        status:
          filterCriteria.companyFilterType === CompanyFilterType.Active
            ? "ACTIVE"
            : undefined,
        searchTerm: filterCriteria.searchTerm,
      });
    }
  }, [filterCriteria]);

  const extractAccounts = (data: any): Account[] => {
    const firstKey = Object.keys(data)[0];
    return data[firstKey]?.edges.map((item: any) => ({
      id: item.node.id,
      businessLegalName: item.node.businessLegalName,
      status: item.node.status,
      numberOfUser: item.node.numberOfUser,
      createdAt: dayjs(item.node.createdAt).format("YYYY/MM/DD"),
      isDeactivateRequested: item.node.isDeactivateRequested,
    }));
  };

  const { dataArray, displayLoading, handleScroll, isInfiniteScrollFetching } =
    useInfiniteScroll({
      query: GET_COMPANIES,
      initialVariables: defaultPaginationVariables,
      extractData: extractAccounts,
      refetchIndex: refreshAccounts,
    });

  const columns: DataColumn<Account>[] = useMemo(
    () => [
      {
        id: "businessLegalName",
        header: t("Account"),
        cell: (account) => (
          <Link
            component={RouterLink}
            to={`/platform/users?companyId=${account.id}`}
            onClick={(event) => event.stopPropagation()}
            variant="body2"
            underline="hover"
            noWrap
            sx={{ display: "block" }}
          >
            {account.businessLegalName}
          </Link>
        ),
      },
      {
        id: "numberOfUser",
        header: t("# of Users"),
        align: "center",
        cell: (account) => account.numberOfUser,
      },
      {
        id: "createdAt",
        header: t("Created"),
        cell: (account) => account.createdAt,
      },
      {
        id: "status",
        header: t("Status"),
        cell: (account) => (
          <RenderAccountStatus
            accountStatus={account.status}
            isDeactivateRequested={account.isDeactivateRequested}
          />
        ),
      },
    ],
    [t],
  );

  const rowActions = (account: Account): RowAction[] => [
    {
      label: t("platform.viewAccountDetails"),
      icon: (
        <HugeiconsIcon
          icon={ViewIcon}
          size={16}
          color="var(--color-text-tertiary)"
        />
      ),
      onClick: () => handleOpenViewDetailsDrawer(account.id),
    },
    ...(account.status === StatusType.ACTIVE
      ? [
          {
            label: t("platform.deactivateAccount"),
            icon: (
              <HugeiconsIcon
                icon={CancelCircleIcon}
                size={16}
                color="var(--color-text-tertiary)"
              />
            ),
            onClick: () => {
              setAccountToDeactivate(account);
              setOpenDeactivateAccountModal(true);
            },
          },
        ]
      : []),
  ];

  return (
    <>
      <DataTable<Account>
        columns={columns}
        rows={dataArray}
        getRowKey={(account) => account.id}
        loading={displayLoading}
        isFetchingMore={isInfiniteScrollFetching}
        onRowClick={(account) => handleOpenViewDetailsDrawer(account.id)}
        onScroll={handleScroll}
        emptyState={t("platform.noAccountsFound")}
        rowActions={rowActions}
        rowActionsLabel={t("RowActions")}
        loadingMoreText={t("LoadingMore")}
        stickyHeader
      />
      {accountToDeactivate && (
        <DeactivateAccountModal
          accountId={accountToDeactivate.id}
          accountName={accountToDeactivate.businessLegalName}
          openDeactivateAccountModal={openDeactivateAccountModal}
          setOpenDeactivateAccountModal={setOpenDeactivateAccountModal}
        />
      )}
    </>
  );
};

export default AccountsTable;
